import { ApiError, ProblemType, errorMessage, type ProblemTypeSlug } from '@/domains/api';

/**
 * A refused admin write, as one line a reviewer can act on.
 *
 * The oversight surface answers three different refusals with the **same** `problems[]`
 * body — `invalid-body` (400, the body's shape), `invalid-query` (400, the query string)
 * and `invalid-action` (422, a well-formed body naming things the response's state does not
 * hold) — and the entries are the only part that says which field was wrong. Showing the
 * title alone turns "usage: the response's pinned version does not carry it" into "The
 * action names things the response's state does not offer", which tells a reviewer to try
 * again with no idea what to change.
 */
export function refusalMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const problems = error.problem?.problems ?? [];
    if (problems.length > 0) {
      return problems.map((problem) => `${problem.target}: ${problem.reason}`).join('; ');
    }

    // The account routes refuse a bad field with ASP.NET's own validation shape instead —
    // `errors: { kind: ["Must be one of advisor, staff."] }` — whose `title` is the
    // contentless "One or more validation errors occurred.". Same job, different key, so it
    // is read here rather than left to fall through to that title. Checked after
    // `problems[]` so nothing already working changes.
    const errors = Object.entries(error.problem?.errors ?? {});
    if (errors.length > 0) {
      return errors.map(([field, reasons]) => `${field}: ${reasons.join(' ')}`).join('; ');
    }
  }
  return errorMessage(error, fallback);
}

/**
 * The same job for a refusal that carries **no body at all**.
 *
 * The account write routes answer most refusals with a bare status code — deliberately, so
 * that nothing leaks from a 403 or a 404 beyond the code itself (AUTH-§11.2). SR-228's own 403s
 * carry a body, and are told by type first (`roleModelRefusal`). A bodiless one leaves
 * `refusalMessage` with nothing to read: the shared client builds its message from
 * `problem.title`, and with no document to parse it falls back to "Request failed with
 * status 403", which tells a reader to try again with no idea what to change.
 *
 * So the copy lives at the call site, keyed by status, because what a 403 *means* differs
 * per route — on an account write it is "your roles no longer include this", on
 * `POST /accounts` "you may only create advisors". A status the caller does not name falls
 * through to whatever the client managed to build: inventing a reason for an unexpected code is
 * worse than an ugly one.
 */
export function statusRefusal(
  error: unknown,
  byStatus: Record<number, string>,
  fallback: string
): string {
  if (error instanceof ApiError) {
    const known = byStatus[error.status];
    if (known) return known;
  }
  return refusalMessage(error, fallback);
}

/**
 * The policy's bodiless 403 on a write that needs `roles.manage` — the roles, the kind, a role's
 * create, edit and delete. The forbidden handler re-reads `/auth/me` on any 403, so the page is
 * drawn again for the roles the caller holds now; signing in again would change nothing.
 */
export const ROLES_FORBIDDEN =
  'This cannot be done — your Team Portal roles no longer include managing roles. The page ' +
  'is read again with your current roles.';

/**
 * What a write whose answer never arrived says — no `ApiError`, or a 5xx, either of which can
 * come after survey-api committed. `what` completes "The answer to …", `then` says what is read
 * again and asks for it to be checked: a retry of a write that landed would be refused, or would
 * land twice.
 */
export function neverArrived(what: string, then: string): string {
  return `The answer to ${what} never arrived, so whether it landed is not known. ${then}`;
}

/**
 * The role model's refusals (SR-228, survey-api's spec §6.1–6.2), told apart by their problem
 * `type` and never by `title` — one sentence each, saying the rule survey-api applied. `null` for
 * any other answer, which the caller says its own way (`refusalMessage`, survey-api's words).
 *
 * Nothing here predicts them: whether a role holds a permission the caller lacks (D5), whether
 * a change leaves nobody able to manage roles, and whether an account is the caller's are
 * survey-api's to judge, and a control is offered whatever this side might guess.
 */
const ROLE_MODEL_REFUSALS: readonly (readonly [ProblemTypeSlug, string])[] = [
  [
    ProblemType.ownAccount,
    'This cannot be done: it is your own account. Ask another administrator.'
  ],
  [
    ProblemType.advisorAccount,
    'The system refused this: an Advisor Portal account holds no Team Portal role, and an ' +
      'account’s portal is fixed when it is made. Make a Team Portal account for this person ' +
      'with Create an account instead.'
  ],
  [
    ProblemType.permissionNotHeld,
    'This cannot be done: it would give a permission your Team Portal roles do not ' +
      'include. Someone holding it has to make this change.'
  ],
  [
    ProblemType.unknownPermission,
    'This cannot be done: it names a permission that does not exist. The permissions ' +
      'are read again — check them before trying again.'
  ],
  [
    ProblemType.lastRoleManager,
    'This cannot be done: no active account would be left able to manage roles. Give ' +
      'another account that permission first.'
  ],
  [ProblemType.roleInUse, 'This cannot be done: an account still holds this role.'],
  [
    ProblemType.unknownRole,
    'This cannot be done: a role chosen no longer exists. The roles are read again — check ' +
      'them before trying again.'
  ]
];

/** The table's sentence for a role-model refusal, or `null` for any other answer. */
export function roleModelRefusal(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError)) return null;
  return ROLE_MODEL_REFUSALS.find(([type]) => error.isProblem(type))?.[1] ?? null;
}

/**
 * Whether a refusal says the page offered something survey-api no longer holds — a permission
 * (`unknown-permission`) or a role (`unknown-role`) — so the caller reads that list again, as
 * `roleModelRefusal`'s sentence for them says it does.
 */
export function namesSomethingGone(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.isProblem(ProblemType.unknownPermission) || error.isProblem(ProblemType.unknownRole))
  );
}

/** What a 400 says of one field — its `problems[]` entry by target, or ASP.NET's `errors` member
 *  (either case) — or null. */
export function fieldRefusal(error: unknown, field: string): Nullable<string> {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const found = error.problem?.problems?.find((problem) => problem.target.toLowerCase() === field.toLowerCase());
  if (found) return found.reason;
  const errors = error.problem?.errors ?? {};
  const key = Object.keys(errors).find((name) => name.toLowerCase() === field.toLowerCase());
  return key && errors[key]?.length ? errors[key][0] : null;
}
